import { motion } from 'framer-motion';
import { Phone, Award } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';
import heroImage from '@/assets/hero-clinica.png';

export default function Hero() {
  const { language } = useLanguage();
  const t = translations[language].hero;

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#e3e6e8]"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#ffa600]/[0.04]" />
        <div className="absolute -bottom-60 -left-60 w-[600px] h-[600px] rounded-full bg-[#5b6770]/[0.03]" />
        <div className="absolute top-1/3 right-1/4 w-[200px] h-[200px] rounded-full bg-[#ffa600]/[0.03]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 md:pt-32 md:pb-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#fff6e5] border border-[#ffa600]/20 rounded-full px-4 py-1.5 mb-6"
              data-testid="hero-experience-badge"
            >
              <Award className="w-4 h-4 text-[#ffa600]" />
              <span className="text-sm font-semibold text-[#ffa600]">{t.experience}</span>
              <span className="text-sm text-[#5b6770]">{t.experienceText}</span>
            </motion.div>

            <h1 className="font-heading font-bold tracking-tight leading-[1.02] mb-6">
              <span className="block text-4xl sm:text-5xl lg:text-6xl text-[#5b6770]">
                {t.title}
              </span>

              <span className="relative inline-block mt-2 pb-4 text-4xl sm:text-5xl lg:text-6xl text-[#ffa600]">
                {t.highlight}
              </span>
            </h1>

            <p className="text-lg text-[#5b6770] leading-relaxed mb-8 max-w-lg">
              {t.subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <motion.a
                href="tel:+34932121044"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 bg-[#ffa600] text-white px-8 py-3.5 rounded-full font-semibold text-base shadow-[0_4px_20px_rgba(255,166,0,0.3)] hover:shadow-[0_6px_30px_rgba(255,166,0,0.4)] hover:bg-[#e69500] transition-all duration-300"
                data-testid="hero-cta-button"
              >
                <Phone className="w-5 h-5" />
                {t.cta}
              </motion.a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
            className="relative order-first lg:order-none"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <img
                src={heroImage}
                alt="Clínica Dental Brandt-Lafuente"
                className="w-full h-[260px] sm:h-[340px] lg:h-[480px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="absolute -bottom-4 left-4 lg:-left-4 bg-white rounded-2xl shadow-lg px-4 py-2.5 sm:px-5 sm:py-3 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-[#fff6e5] rounded-xl flex items-center justify-center">
                <span className="text-[#ffa600] text-lg font-bold">4.8</span>
              </div>
              <div>
                <div className="text-[#ffa600] text-sm">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
                <p className="text-xs text-[#5b6770]">Google Reviews</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
